import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAppAuth } from "./AuthContext";
import { ft } from "./api";
import { getAttribution } from "../lib/attribution";
import { AuthStage, PremiumAuthCard } from "./PremiumStage";
import { T } from "../lib/fc3i18n";

/**
 * Free, worldwide sign-up: name + email + password + DOB → 6-digit code by EMAIL.
 * Backed by Fertile-Track's existing email-OTP endpoints via the web adapter
 * (/api/web/auth/email/*). No SMS, no Firebase billing.
 */
const API_BASE = process.env.REACT_APP_BACKEND_URL || "";
async function post(path, body) {
  const res = await fetch(`${API_BASE}${path}`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body), credentials: "include" });
  const text = await res.text();
  let data = null; try { data = text ? JSON.parse(text) : null; } catch { data = text; }
  if (!res.ok) {
    const d = data && data.detail;
    const msg = Array.isArray(d) ? d.map((e) => (e && e.msg) || String(e)).join("; ") : (typeof d === "string" ? d : `HTTP ${res.status}`);
    const err = new Error(msg); err.status = res.status; throw err;
  }
  return data;
}

const COUNTRIES = ["India", "United States", "United Kingdom", "Canada", "Australia", "United Arab Emirates", "Saudi Arabia", "Singapore", "Malaysia", "Indonesia", "Philippines", "Pakistan", "Bangladesh", "Sri Lanka", "Nepal", "Nigeria", "Kenya", "South Africa", "Germany", "France", "Spain", "Italy", "Netherlands", "Brazil", "Mexico", "Other"];

function guessCountry() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    if (/Kolkata|Calcutta/.test(tz)) return "India";
    if (/^America\/(New_York|Chicago|Denver|Los_Angeles|Phoenix|Anchorage)/.test(tz)) return "United States";
    if (tz === "Europe/London") return "United Kingdom";
    if (tz === "Asia/Dubai") return "United Arab Emirates";
    if (tz === "Asia/Singapore") return "Singapore";
    if (tz.startsWith("Australia/")) return "Australia";
  } catch { /* ignore */ }
  return "";
}

const field = "mt-2 w-full rounded-xl border px-4 py-3 focus:outline-none";
const lbl = "text-[11px] font-bold uppercase tracking-[0.14em]";

export default function EmailSignup() {
  const { refresh } = useAppAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [dob, setDob] = useState("");
  const [country, setCountry] = useState(guessCountry);
  const [stage, setStage] = useState("form"); // form | code
  const [digits, setDigits] = useState(["", "", "", "", "", ""]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [resendIn, setResendIn] = useState(0);
  const boxes = useRef([]);

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const dobValid = /^\d{2}-\d{2}-\d{4}$/.test(dob) && (() => {
    const [d, m, y] = dob.split("-").map(Number);
    const dt = new Date(y, m - 1, d);
    if (dt.getFullYear() !== y || dt.getMonth() !== m - 1 || dt.getDate() !== d) return false;
    const age = (Date.now() - dt.getTime()) / (365.25 * 864e5);
    return age >= 13 && age < 110;
  })();
  const formValid = name.trim() && emailValid && password.length >= 6 && dobValid;
  const code = digits.join("");

  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const t = setTimeout(() => setResendIn((v) => v - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const sendCode = useCallback(async () => {
    setError(null); setBusy(true);
    try {
      await post("/api/web/auth/email/send-otp", { name: name.trim(), email: email.trim(), password, dob, country: country || undefined });
      setStage("code"); setDigits(["", "", "", "", "", ""]); setResendIn(45);
      setTimeout(() => boxes.current[0]?.focus(), 60);
    } catch (e) {
      setError(e.message && !/^HTTP/.test(e.message) ? e.message : T("We couldn't send the code. Please check your email and try again."));
    } finally { setBusy(false); }
  }, [name, email, password, dob, country]);

  const verify = useCallback(async (c) => {
    setError(null); setBusy(true);
    try {
      await post("/api/web/auth/email/verify-otp", { name: name.trim(), email: email.trim(), password, otp: c });
      await refresh();
      try { const snap = getAttribution(); if (snap.first || snap.last) ft.attachAttribution(snap); } catch { /* never block */ }
      nav("/app/home");
    } catch (e) {
      setError(e.message && !/^HTTP/.test(e.message) ? e.message : T("Invalid or expired code."));
      setDigits(["", "", "", "", "", ""]);
      setTimeout(() => boxes.current[0]?.focus(), 30);
    } finally { setBusy(false); }
  }, [name, email, password, refresh, nav]);

  useEffect(() => { if (stage === "code" && /^\d{6}$/.test(code) && !busy) verify(code); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  const setDigit = (i, v) => {
    const c = v.replace(/\D/g, "").slice(-1);
    setDigits((d) => { const n = [...d]; n[i] = c; return n; });
    if (c && i < 5) boxes.current[i + 1]?.focus();
  };
  const onPaste = (e) => {
    const t = (e.clipboardData?.getData("text") || "").replace(/\D/g, "").slice(0, 6);
    if (!t) return; e.preventDefault();
    setDigits(t.padEnd(6, " ").split("").map((x) => (x === " " ? "" : x)));
  };

  return (
    <div className="fc-auth-root" data-mode="signup">
      <AuthStage isSignup />
      <PremiumAuthCard
        title={stage === "form" ? T("Create your FemCare account") : T("Check your email")}
        subtitle={stage === "form"
          ? T("Free, private and the same account on iOS, Android and the web. We'll email you a 6-digit code.")
          : <>{T("We've sent a 6-digit code to")} <b style={{ color: "#1C1424" }}>{email.trim()}</b>. {T("It can take a minute — check spam too.")}</>}
        footer={<div data-testid="auth-cross-link">{T("Already have an account?")} <Link to="/app/login" className="font-semibold" data-testid="link-to-login">{T("Sign in")}</Link></div>}>
        {stage === "form" ? (
          <form onSubmit={(e) => { e.preventDefault(); if (formValid && !busy) sendCode(); }} className="space-y-4" data-testid="email-signup-form">
            <label className="block"><span className={lbl}>{T("Full name")} *</span>
              <input className={field} value={name} onChange={(e) => setName(e.target.value)} required maxLength={120} autoComplete="name" placeholder={T("Your full name")} data-testid="signup-full-name" /></label>
            <label className="block"><span className={lbl}>Email *</span>
              <input className={field} type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email" placeholder="you@example.com" data-testid="signup-email" />
              {email && !emailValid && <p className="text-[12px] text-[#B04A6A] mt-1">{T("Please enter a valid email address")}</p>}</label>
            <label className="block"><span className={lbl}>{T("Password")} *</span>
              <input className={field} type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} autoComplete="new-password" placeholder={T("Minimum 6 characters")} data-testid="signup-password" /></label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block"><span className={lbl}>{T("Date of birth")} *</span>
                <input className={field} value={dob} inputMode="numeric" maxLength={10} placeholder="DD-MM-YYYY" data-testid="signup-dob"
                  onChange={(e) => { const r = e.target.value.replace(/\D/g, "").slice(0, 8); setDob(r.length > 4 ? `${r.slice(0, 2)}-${r.slice(2, 4)}-${r.slice(4)}` : r.length > 2 ? `${r.slice(0, 2)}-${r.slice(2)}` : r); }} /></label>
              <label className="block"><span className={lbl}>{T("Country")}</span>
                <select className={field} value={country} onChange={(e) => setCountry(e.target.value)} data-testid="signup-country">
                  <option value="">{T("Select")}</option>
                  {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select></label>
            </div>
            {dob && !dobValid && <p className="text-[12px] text-[#B04A6A] -mt-2">{T("Please enter a valid date of birth (you must be at least 13)")}</p>}
            {error && <p className="text-sm text-[#B04A6A]" role="alert" data-testid="signup-error">{error}</p>}
            <button type="submit" disabled={!formValid || busy} className="w-full" data-testid="signup-send-code">
              {busy ? T("Sending code…") : T("Email me a code")}
            </button>
            <p className="text-[12px] text-[#7A6F80] text-center">
              {T("By continuing, you agree to our")} <Link to="/terms">{T("Terms")}</Link> & <Link to="/privacy">{T("Privacy Policy")}</Link>.
            </p>
          </form>
        ) : (
          <form onSubmit={(e) => { e.preventDefault(); if (/^\d{6}$/.test(code) && !busy) verify(code); }} className="space-y-4" data-testid="email-code-form">
            <fieldset>
              <legend className="sr-only">{T("6-digit code")}</legend>
              <div className="flex justify-between gap-2" onPaste={onPaste}>
                {digits.map((d, i) => (
                  <input key={i} ref={(el) => { boxes.current[i] = el; }} value={d} inputMode="numeric" maxLength={1} aria-label={`Digit ${i + 1}`}
                    data-testid={`otp-digit-${i}`} disabled={busy}
                    onChange={(e) => setDigit(i, e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Backspace" && !digits[i] && i > 0) boxes.current[i - 1]?.focus(); }}
                    className="w-12 h-14 md:w-14 md:h-16 text-center text-2xl font-extrabold rounded-xl border" />
                ))}
              </div>
            </fieldset>
            {error && <p className="text-sm text-[#B04A6A]" role="alert" data-testid="code-error">{error}</p>}
            <button type="submit" disabled={!/^\d{6}$/.test(code) || busy} className="w-full" data-testid="signup-verify">
              {busy ? T("Verifying…") : T("Verify & create account")}
            </button>
            <div className="flex items-center justify-between text-[13px]">
              <button type="button" onClick={() => { setStage("form"); setError(null); }} className="font-semibold text-[#6E6478] hover:text-[#1C1424]">← {T("Change email")}</button>
              {resendIn > 0
                ? <span className="text-[#7A6F80]">{T("Resend in {{n}}s", { n: resendIn })}</span>
                : <button type="button" onClick={sendCode} disabled={busy} className="font-semibold text-[#D62E6E]" data-testid="signup-resend">{T("Resend code")}</button>}
            </div>
          </form>
        )}
      </PremiumAuthCard>
    </div>
  );
}
